import { Box, Button, Typography } from "@mui/material";
import useAPI from "../../../../../../../../api/hooks/useAPI";
import { useCheckUserPermission } from "../../../../../../../../api/hooks/useCheckUserPermission";
import useColors from "../../../../../../../../api/hooks/useColors";
import CustomToolTipWithPermission from "../../../../../../../../components/CustomToolTipWithPermission";
import { ReactComponent as mailSVG } from "../../../../../../../../icons/icon_files/mail_24.svg";
import SvgToIcon from "../../../../../../../../icons/SvgToIcon";
import {
  GoogleFileHeader,
  PLATFORM_USER_ACTIONS,
  SHARED_GOOGLE_DRIVE_FILE_CONTROL_IDS,
} from "../../../../../../../../utils/constants";
import { isEmailValid } from "../../../../../../../../utils/utils";

const getFileOwnerEmail = (fullControl, misconfiguration) => {
  const ownerColumnIndex =
    fullControl.misconfigurations_extra_info_headers.indexOf(
      GoogleFileHeader.OWNER
    );
  return misconfiguration.extra_info[ownerColumnIndex];
};

const getFileType = (fullControl, misconfiguration) => {
  const type = (() => {
    const entry = misconfiguration?.extra_info?.find((info) =>
      info?.startsWith(GoogleFileHeader.TYPE)
    );
    if (!entry) return undefined;
    const firstDash = entry.indexOf("-");
    return firstDash === -1 ? undefined : entry.slice(firstDash + 1);
  })();
  return type;
};

const getFileLink = (fullControl, misconfiguration) => {
  const documentId = (() => {
    const entry = misconfiguration?.extra_info?.find((info) =>
      info?.startsWith(GoogleFileHeader.DOCUMENT_ID)
    );
    if (!entry) return undefined;
    const firstDash = entry.indexOf("-");
    return firstDash === -1 ? undefined : entry.slice(firstDash + 1);
  })();
  return `https://drive.google.com/open?id=${documentId}`;
};

const createEmailContent = (fullControl, misconfiguration) => {
  const fileName = misconfiguration.display_name;
  const fileLink = getFileLink(fullControl, misconfiguration);
  let emailSubject = "";
  let emailBody = "";
  if (SHARED_GOOGLE_DRIVE_FILE_CONTROL_IDS.includes(fullControl.control_id)) {
    const fileType = getFileType(fullControl, misconfiguration);
    // Aligned with backend: google/analyzers/drive/drive_analyzer_base.py
    const isFolder = fileType === "folder";
    emailSubject = "Action Required: Public File Sharing Detected";
    emailBody = `The ${
      isFolder ? "folder" : "file"
    } ${fileName} you own is currently shared publicly, allowing anonymous access to its content.%0APlease review the details and update the sharing permissions if this was unintentional.%0A%0A${fileLink}`;
  } else {
    emailSubject = "Action Required: Sensitive File Download Detected";
    emailBody = `The file ${fileName} you own is currently downloaded, allowing anonymous access to its content.%0APlease review the details and update the sharing permissions if this was unintentional.%0A%0A${fileLink}`;
  }
  return [emailSubject, emailBody];
};

export default function NotifyFileOwnerButton({
  fullControl,
  misconfiguration,
}) {
  const { trackEvent } = useAPI();
  const colors = useColors();

  const { permitted: hasPermission } = useCheckUserPermission(
    PLATFORM_USER_ACTIONS.NOTIFY_USER_ON_GOOGLE_DRIVE_FILES
  );

  const fileOwnerEmail = getFileOwnerEmail(fullControl, misconfiguration);
  const [emailSubject, emailBody] = createEmailContent(
    fullControl,
    misconfiguration
  );

  const tooltipText = isEmailValid(fileOwnerEmail)
    ? ""
    : "File owner email is invalid";

  const handleClick = () => {
    trackEvent(
      `User clicked the notify Google Drive file owner button in control ${fullControl.control_id}`
    );
    window.location.href = `mailto:${fileOwnerEmail}?subject=${emailSubject}&body=${emailBody}`;
  };

  const isDisabled = !isEmailValid(fileOwnerEmail) || !hasPermission;

  return (
    <CustomToolTipWithPermission
      title={tooltipText}
      permission={hasPermission}
      placement="top"
    >
      <Box width="100%">
        <Button
          onClick={handleClick}
          disableRipple
          disabled={isDisabled}
          sx={{
            display: "flex",
            justifyContent: "flex-start",
            color: colors.text_primary,
            border: "0px solid " + colors.grey_600,
            width: "100%",
            height: "36px",
            textTransform: "none",
            borderRadius: "10px",
            paddingLeft: "5px",
            gap: 1,
          }}
        >
          <SvgToIcon IconLogo={mailSVG} width={"20px"} />
          <Typography variant="caption">Notify file owner</Typography>
        </Button>
      </Box>
    </CustomToolTipWithPermission>
  );
}
